import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { ComplianceCaseTypeEnum as CaseType } from "~/graphql/supergraphEnums";
import {
  GetCaseWithRelationshipsDocument,
  GetOnboardingCasesForPositionsDocument,
  GetPositionsForCaseDocument,
} from "~/graphql/supergraphHooks";
import {
  type ComplianceCaseTypeEnum,
  type GetCaseWithRelationshipsQuery,
  type GetOnboardingCasesForPositionsQuery,
  type GetPositionsForCaseQuery,
} from "~/graphql/supergraphTypes";
import { formatDate } from "~/pages/Billing/utils";
import { ProfileNameFields } from "~/pages/Compliance/utils/types";
import { toGlobalId } from "~/utils/graphql-helpers";
import { getInvestingProfileName } from "../../../Profiles/utils";
import {
  ArenaEntitiesRootProfile,
  detail,
  ImpactedGPRow,
  ProfileLinkedComplianceCases,
} from "./types";

type PositionNodeFromQuery = NonNullable<
  NonNullable<GetOnboardingCasesForPositionsQuery["positions"]>["edges"][number]
>["node"];

export const collectArenaEntities = (
  arr: ProfileLinkedComplianceCases[],
): ArenaEntitiesRootProfile => {
  const arenaMap: Record<number, detail> = {};
  arr.forEach((item) => {
    const arenaId = item.complianceCase?.externalArena?.id;
    const caseId = item.caseId;
    const externalEntity = item.complianceCase?.externalEntity;
    const investingProfile = item.complianceCase?.investingProfile;
    const program = item.complianceCase?.program;

    if (!arenaId || !externalEntity) return;

    if (!arenaMap[arenaId]) {
      arenaMap[arenaId] = [];
    }
    arenaMap[arenaId].push({
      caseId: caseId,
      externalEntities: externalEntity,
      investingProfiles: investingProfile,
      program: program,
    });
  });
  // Convert the map to an array
  return Object.entries(arenaMap).map(([arenaId, detail]) => ({
    arenaId: Number(arenaId),
    detail,
  }));
};

export const collectArenaEntitiesFromPositions = (
  positions: PositionNodeFromQuery[],
): ArenaEntitiesRootProfile => {
  const arenaMap: Record<number, detail> = {};

  positions.forEach((position) => {
    if (!position) return;
    const onboardingCase = position.onboardingCase;
    if (!onboardingCase) return;

    const arenaId = onboardingCase.externalArena?.id;
    const externalEntity = onboardingCase.externalEntity;
    const investingProfile = onboardingCase.investingProfile;
    const program = onboardingCase.program;

    if (!arenaId || !externalEntity) return;

    if (!arenaMap[arenaId]) {
      arenaMap[arenaId] = [];
    }
    arenaMap[arenaId].push({
      caseId: onboardingCase.id,
      externalEntities: { name: externalEntity.name ?? "" },
      investingProfiles: {
        id: investingProfile?.id || 0,
        firstName: investingProfile?.firstName ?? undefined,
        middleName: investingProfile?.middleName ?? undefined,
        lastName: investingProfile?.lastName ?? undefined,
        legalName: investingProfile?.legalName ?? undefined,
        complianceObjectType: investingProfile?.complianceObjectType ?? undefined,
      },
      program: { name: program?.name ?? "" },
    });
  });

  return Object.entries(arenaMap).map(([arenaId, detail]) => ({
    arenaId: Number(arenaId),
    detail,
  }));
};

export const rowsFromQuery = (
  data: GetCaseWithRelationshipsQuery["complianceCase"],
  positionsData: GetPositionsForCaseQuery["complianceProfile"],
  caseType: ComplianceCaseTypeEnum,
): ImpactedGPRow[] => {
  if (!data || !positionsData) return [];

  const relevantCases = caseType === CaseType.Escalation ? data.peerCases : data.childCases;

  // Legacy logic using complianceCases
  const entities: ArenaEntitiesRootProfile = collectArenaEntities(
    positionsData?.complianceCases as ProfileLinkedComplianceCases[],
  );

  const uniqueArenaCases = positionsData?.complianceCases.filter(
    (item, index, self) =>
      item.complianceCase?.externalArena?.id !== undefined &&
      self.findIndex(
        (i) => i.complianceCase?.externalArena?.id === item.complianceCase?.externalArena?.id,
      ) === index,
  );

  return (
    uniqueArenaCases?.map((caseItem) => {
      const gpName = caseItem.complianceCase?.externalArena?.name || "Unknown GP";

      let caseData = relevantCases.find(
        (c) => c.externalArena?.id === caseItem.complianceCase?.externalArena?.id,
      );
      // fallback to case data if peerCase is not found
      if (caseType === CaseType.Escalation && caseData === undefined) {
        caseData = {
          id: data?.id,
          name: data?.name || getInvestingProfileName(data?.investingProfile) || "Unknown Case",
          caseType: data?.caseType,
          createdAt: data?.createdAt,
          investingProfile: data?.investingProfile ?? null,
          externalEntity: data?.externalEntity ?? null,
          externalArena: data?.externalArena ?? null,
          program: data?.program ?? null,
        };
      }
      const positionCount =
        entities.find((entity) => entity.arenaId === caseItem.complianceCase?.externalArena?.id)
          ?.detail.length || 1;

      const matchingEntity = entities.find(
        (entity) => entity.arenaId === caseItem.complianceCase?.externalArena?.id,
      );
      const details =
        matchingEntity?.detail.map((detail) => ({
          positionName: {
            id: detail.caseId,
            name:
              detail.investingProfiles.complianceObjectType === "individual"
                ? `${detail.investingProfiles.firstName} ${detail.investingProfiles.lastName} in ${detail.externalEntities.name}`
                : `${detail.investingProfiles.legalName} in ${detail.externalEntities.name}`,
          },
          program: detail.program.name,
          investingProfileId: detail.investingProfiles.id,
        })) || [];

      return {
        GP: gpName,
        arenaId: caseItem.complianceCase?.externalArena?.id ?? 0,
        position: positionCount,
        case: {
          id: caseItem.caseId || 0,
          name: caseData?.name || getInvestingProfileName(caseData?.investingProfile),
        },
        date: formatDate(caseData?.createdAt ?? null),
        details: details,
      };
    }) || []
  );
};

export const rowsFromPositionsData = (
  data: GetCaseWithRelationshipsQuery["complianceCase"],
  onboardingCasesData: GetOnboardingCasesForPositionsQuery["positions"],
  caseType: ComplianceCaseTypeEnum,
): ImpactedGPRow[] => {
  if (!data || !onboardingCasesData?.edges) return [];

  const relevantCases = caseType === CaseType.Escalation ? data.peerCases : data.childCases;

  // Extract position nodes from edges
  const positionNodes = onboardingCasesData.edges
    .map((edge) => edge?.node)
    .filter((node): node is PositionNodeFromQuery => node !== null && node !== undefined);

  // Collect arena entities from positions
  const entities = collectArenaEntitiesFromPositions(positionNodes);

  // Get unique arenas from positions (filter by arenaId)
  const positionsWithUniqueArenas = positionNodes.filter(
    (position, index, self) =>
      position?.onboardingCase?.externalArena?.id !== undefined &&
      self.findIndex(
        (p) =>
          p?.onboardingCase?.externalArena?.id === position?.onboardingCase?.externalArena?.id,
      ) === index,
  );

  return positionsWithUniqueArenas.map((position) => {
    const arenaId = position?.onboardingCase?.externalArena?.id;
    const gpName = position?.onboardingCase?.externalArena?.name || "Unknown GP";

    // Find case data from relevantCases with matching arena
    let caseData = relevantCases.find((c) => c.externalArena?.id === arenaId);

    // Fallback to main case data if peerCase is not found
    if (caseType === CaseType.Escalation && caseData === undefined) {
      caseData = {
        id: data?.id,
        name: data?.name || getInvestingProfileName(data?.investingProfile) || "Unknown Case",
        caseType: data?.caseType,
        createdAt: data?.createdAt,
        investingProfile: data?.investingProfile ?? null,
        externalEntity: data?.externalEntity ?? null,
        externalArena: data?.externalArena ?? null,
        program: data?.program ?? null,
      };
    }

    // Count positions per arena
    const positionCount =
      entities.find((entity) => entity.arenaId === arenaId)?.detail.length || 1;

    // Build details from positions with matching arena (from onboardingCase)
    const matchingEntity = entities.find((entity) => entity.arenaId === arenaId);
    const details =
      matchingEntity?.detail.map((detail) => ({
        positionName: {
          id: detail.caseId,
          name: `${getInvestingProfileName(detail.investingProfiles as ProfileNameFields)} in ${detail.externalEntities.name}`,
        },
        program: detail.program.name,
        investingProfileId: detail.investingProfiles.id,
      })) || [];

    return {
      GP: gpName,
      arenaId: arenaId ?? 0,
      position: positionCount,
      case: {
        id: caseData?.id || 0,
        name: caseData?.name || getInvestingProfileName(caseData?.investingProfile),
      },
      date: formatDate(caseData?.createdAt ?? null),
      details: details,
    };
  });
};

export class ImpactedGPGraphqlDataSource implements AgGrid.IServerSideDatasource {
  private apiClient: ApolloClient<NormalizedCacheObject>;
  private caseId: number;
  private caseType: ComplianceCaseTypeEnum;
  private profileId: number;
  private onCountChange: (count: number) => void;
  private useReusableProfiles: boolean;

  constructor(
    caseId: number,
    caseType: ComplianceCaseTypeEnum,
    profileId: number,
    onCountChange: (count: number) => void,
    useReusableProfiles = false,
  ) {
    this.apiClient = client;
    this.caseId = caseId;
    this.caseType = caseType;
    this.profileId = profileId;
    this.onCountChange = onCountChange;
    this.useReusableProfiles = useReusableProfiles;
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const caseRelationshipQuery = this.apiClient.query<GetCaseWithRelationshipsQuery>({
      query: GetCaseWithRelationshipsDocument,
      variables: {
        caseId: this.caseId,
      },
      fetchPolicy: "no-cache",
    });

    const positionQuery = this.apiClient.query<GetPositionsForCaseQuery>({
      query: GetPositionsForCaseDocument,
      variables: {
        profileId: this.profileId,
        caseTypes: [CaseType.InvestorOnboarding, CaseType.Remediation],
      },
      fetchPolicy: "no-cache",
    });

    Promise.all([caseRelationshipQuery, positionQuery])
      .then(([caseResponse, positionResponse]) => {
        // If reusable profiles flag is enabled, fetch onboarding cases using position IDs
        if (this.useReusableProfiles && positionResponse.data.complianceProfile?.positions) {
          const positionIds = positionResponse.data.complianceProfile.positions
            .map((p) => p.id)
            .filter((id): id is string => id !== null && id !== undefined);

          if (positionIds.length === 0) {
            this.onCountChange(0);
            params.success({ rowData: [], rowCount: 0 });
            return;
          }

          // Get arena IDs from clients array on the profile
          const clients = positionResponse.data.complianceProfile?.clients || [];
          const globalArenaIds = clients.map((clientId: number) =>
            toGlobalId("ArenaObjectType", clientId),
          );
          // positionIds are already global IDs from the query, don't encode them again
          const globalPositionIds = positionIds;

          // Fetch onboarding cases for the position IDs
          this.apiClient
            .query<GetOnboardingCasesForPositionsQuery>({
              query: GetOnboardingCasesForPositionsDocument,
              variables: {
                positionFilters: {
                  arenaIds: globalArenaIds,
                  positionIds: globalPositionIds,
                  includeFundAdminEntities: false,
                },
              },
              fetchPolicy: "no-cache",
            })
            .then((onboardingCasesResponse) => {
              const rowData = rowsFromPositionsData(
                caseResponse.data.complianceCase,
                onboardingCasesResponse.data.positions,
                this.caseType,
              );
              const rowCount = rowData.length;
              this.onCountChange(rowCount);

              params.success({
                rowData: rowData,
                rowCount: rowCount,
              });
            })
            .catch(() => {
              params.fail();
            });
        } else {
          // Legacy logic using complianceCases
          const rowData = rowsFromQuery(
            caseResponse.data.complianceCase,
            positionResponse.data.complianceProfile,
            this.caseType,
          );
          const rowCount = rowData.length;
          this.onCountChange(rowCount);

          params.success({
            rowData: rowData,
            rowCount: rowCount,
          });
        }
      })
      .catch(() => {
        params.fail();
      });
  }
}
